import { useState, useEffect } from 'react';
import axios from 'axios';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Plus, Trash, Package, Users, ShoppingBag } from '@phosphor-icons/react';
import Navbar from '@/components/custom/Navbar';
import Footer from '@/components/custom/Footer';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = ['Apparel', 'Jewelry', 'Homeware', 'Accessories', 'Bags', 'Footwear'];

const AdminPage = () => {
  const [products, setProducts] = useState([]);
  const [artisans, setArtisans] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showProductDialog, setShowProductDialog] = useState(false);
  const [showArtisanDialog, setShowArtisanDialog] = useState(false);

  const [productForm, setProductForm] = useState({
    name: '', description: '', price: '', category: '', color: '', size: '',
    material: '', material_origin: '', artisan_id: '', craftsmanship_method: '',
    time_spent_hours: '', images: [], story_tag: ''
  });

  const [artisanForm, setArtisanForm] = useState({
    name: '', location: '', craft_specialty: '', story: '', image_url: '', years_experience: ''
  });

  const fetchData = async () => {
    try {
      const [productsRes, artisansRes, ordersRes] = await Promise.all([
        axios.get(`${API}/products`),
        axios.get(`${API}/artisans`),
        axios.get(`${API}/admin/orders`)
      ]);
      setProducts(productsRes.data);
      setArtisans(artisansRes.data);
      setOrders(ordersRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleCreateArtisan = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/artisans`, {
        ...artisanForm,
        years_experience: parseInt(artisanForm.years_experience) || 0
      });
      toast.success('Artisan created successfully');
      setShowArtisanDialog(false);
      setArtisanForm({ name: '', location: '', craft_specialty: '', story: '', image_url: '', years_experience: '' });
      fetchData();
    } catch (error) {
      toast.error('Failed to create artisan');
    }
  };

  const handleCreateProduct = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/products`, {
        ...productForm,
        price: parseFloat(productForm.price),
        time_spent_hours: parseFloat(productForm.time_spent_hours),
        images: productForm.images.filter(Boolean)
      });
      toast.success('Product created successfully');
      setShowProductDialog(false);
      setProductForm({
        name: '', description: '', price: '', category: '', color: '', size: '',
        material: '', material_origin: '', artisan_id: '', craftsmanship_method: '',
        time_spent_hours: '', images: [], story_tag: ''
      });
      fetchData();
    } catch (error) {
      toast.error('Failed to create product');
    }
  };

  const handleDeleteProduct = async (productId) => {
    if (!window.confirm('Are you sure you want to delete this product?')) return;
    try {
      await axios.delete(`${API}/admin/products/${productId}`);
      toast.success('Product deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete product');
    }
  };

  const handleUpdateOrderStatus = async (orderId, status) => {
    try {
      await axios.put(`${API}/admin/orders/${orderId}/status?status=${status}`);
      toast.success('Order status updated');
      fetchData();
    } catch (error) {
      toast.error('Failed to update order status');
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      
      {/* Header */}
      <section className="pt-24 pb-8 bg-secondary/30">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <Badge variant="outline" className="mb-4">Admin Panel</Badge>
          <h1 className="text-3xl md:text-4xl font-medium" style={{ fontFamily: 'Playfair Display, serif' }}>
            Manage TDG Trace
          </h1>
        </div>
      </section>

      {/* Admin Content */}
      <section className="py-8">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <Tabs defaultValue="products" className="w-full" data-testid="admin-tabs">
            <TabsList className="mb-8">
              <TabsTrigger value="products" className="gap-2">
                <Package weight="duotone" className="w-4 h-4" />
                Products ({products.length})
              </TabsTrigger>
              <TabsTrigger value="artisans" className="gap-2">
                <Users weight="duotone" className="w-4 h-4" />
                Artisans ({artisans.length})
              </TabsTrigger>
              <TabsTrigger value="orders" className="gap-2">
                <ShoppingBag weight="duotone" className="w-4 h-4" />
                Orders ({orders.length})
              </TabsTrigger>
            </TabsList>

            {/* Products Tab */}
            <TabsContent value="products">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-medium">All Products</h2>
                <Dialog open={showProductDialog} onOpenChange={setShowProductDialog}>
                  <DialogTrigger asChild>
                    <Button className="gap-2" data-testid="add-product-btn">
                      <Plus weight="bold" className="w-4 h-4" />
                      Add Product
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle>Add New Product</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleCreateProduct} className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label>Name</Label>
                          <Input value={productForm.name} onChange={e => setProductForm({...productForm, name: e.target.value})} required />
                        </div>
                        <div>
                          <Label>Price ($)</Label>
                          <Input type="number" step="0.01" value={productForm.price} onChange={e => setProductForm({...productForm, price: e.target.value})} required />
                        </div>
                      </div>
                      <div>
                        <Label>Description</Label>
                        <Textarea value={productForm.description} onChange={e => setProductForm({...productForm, description: e.target.value})} required />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label>Category</Label>
                          <Select value={productForm.category} onValueChange={val => setProductForm({...productForm, category: val})}>
                            <SelectTrigger><SelectValue placeholder="Select category" /></SelectTrigger>
                            <SelectContent>
                              {CATEGORIES.map(cat => <SelectItem key={cat} value={cat}>{cat}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label>Artisan</Label>
                          <Select value={productForm.artisan_id} onValueChange={val => setProductForm({...productForm, artisan_id: val})}>
                            <SelectTrigger><SelectValue placeholder="Select artisan" /></SelectTrigger>
                            <SelectContent>
                              {artisans.map(a => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <div className="grid grid-cols-3 gap-4">
                        <div>
                          <Label>Color</Label>
                          <Input value={productForm.color} onChange={e => setProductForm({...productForm, color: e.target.value})} required />
                        </div>
                        <div>
                          <Label>Size</Label>
                          <Input value={productForm.size} onChange={e => setProductForm({...productForm, size: e.target.value})} required />
                        </div>
                        <div>
                          <Label>Time (hours)</Label>
                          <Input type="number" value={productForm.time_spent_hours} onChange={e => setProductForm({...productForm, time_spent_hours: e.target.value})} required />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label>Material</Label>
                          <Input value={productForm.material} onChange={e => setProductForm({...productForm, material: e.target.value})} required />
                        </div>
                        <div>
                          <Label>Material Origin</Label>
                          <Input value={productForm.material_origin} onChange={e => setProductForm({...productForm, material_origin: e.target.value})} required />
                        </div>
                      </div>
                      <div>
                        <Label>Craftsmanship Method</Label>
                        <Input value={productForm.craftsmanship_method} onChange={e => setProductForm({...productForm, craftsmanship_method: e.target.value})} required />
                      </div>
                      <div>
                        <Label>Image URL</Label>
                        <Input value={productForm.images[0] || ''} onChange={e => setProductForm({...productForm, images: [e.target.value]})} />
                      </div>
                      <div>
                        <Label>Story Tag (optional)</Label>
                        <Input value={productForm.story_tag} onChange={e => setProductForm({...productForm, story_tag: e.target.value})} />
                      </div>
                      <Button type="submit" className="w-full">Create Product</Button>
                    </form>
                  </DialogContent>
                </Dialog>
              </div>

              <Card>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Product</TableHead>
                      <TableHead>Category</TableHead>
                      <TableHead>Price</TableHead>
                      <TableHead>Score</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {products.map(product => (
                      <TableRow key={product.id}>
                        <TableCell className="font-medium">{product.name}</TableCell>
                        <TableCell>{product.category}</TableCell>
                        <TableCell>${product.price?.toFixed(2)}</TableCell>
                        <TableCell>{product.transparency_score?.toFixed(0)}</TableCell>
                        <TableCell>
                          <Button variant="ghost" size="icon" onClick={() => handleDeleteProduct(product.id)}>
                            <Trash className="w-4 h-4 text-destructive" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Card>
            </TabsContent>

            {/* Artisans Tab */}
            <TabsContent value="artisans">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-medium">All Artisans</h2>
                <Dialog open={showArtisanDialog} onOpenChange={setShowArtisanDialog}>
                  <DialogTrigger asChild>
                    <Button className="gap-2" data-testid="add-artisan-btn">
                      <Plus weight="bold" className="w-4 h-4" />
                      Add Artisan
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Add New Artisan</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleCreateArtisan} className="space-y-4">
                      <div>
                        <Label>Name</Label>
                        <Input value={artisanForm.name} onChange={e => setArtisanForm({...artisanForm, name: e.target.value})} required />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label>Location</Label>
                          <Input value={artisanForm.location} onChange={e => setArtisanForm({...artisanForm, location: e.target.value})} required />
                        </div>
                        <div>
                          <Label>Years Experience</Label>
                          <Input type="number" value={artisanForm.years_experience} onChange={e => setArtisanForm({...artisanForm, years_experience: e.target.value})} />
                        </div>
                      </div>
                      <div>
                        <Label>Craft Specialty</Label>
                        <Input value={artisanForm.craft_specialty} onChange={e => setArtisanForm({...artisanForm, craft_specialty: e.target.value})} required />
                      </div>
                      <div>
                        <Label>Story</Label>
                        <Textarea value={artisanForm.story} onChange={e => setArtisanForm({...artisanForm, story: e.target.value})} required />
                      </div>
                      <div>
                        <Label>Image URL (optional)</Label>
                        <Input value={artisanForm.image_url} onChange={e => setArtisanForm({...artisanForm, image_url: e.target.value})} />
                      </div>
                      <Button type="submit" className="w-full">Create Artisan</Button>
                    </form>
                  </DialogContent>
                </Dialog>
              </div>

              <Card>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Location</TableHead>
                      <TableHead>Specialty</TableHead>
                      <TableHead>Experience</TableHead>
                      <TableHead>Products</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {artisans.map(artisan => (
                      <TableRow key={artisan.id}>
                        <TableCell className="font-medium">{artisan.name}</TableCell>
                        <TableCell>{artisan.location}</TableCell>
                        <TableCell>{artisan.craft_specialty}</TableCell>
                        <TableCell>{artisan.years_experience} years</TableCell>
                        <TableCell>{artisan.products_created}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Card>
            </TabsContent>

            {/* Orders Tab */}
            <TabsContent value="orders">
              <h2 className="text-xl font-medium mb-6">All Orders</h2>
              <Card>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Order ID</TableHead>
                      <TableHead>Customer</TableHead>
                      <TableHead>Total</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Payment</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {orders.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} className="text-center text-muted-foreground py-8">
                          No orders yet
                        </TableCell>
                      </TableRow>
                    ) : (
                      orders.map(order => (
                        <TableRow key={order.id}>
                          <TableCell className="font-mono text-xs">{order.id.slice(0, 8)}...</TableCell>
                          <TableCell>{order.customer_name}</TableCell>
                          <TableCell>${order.total_amount?.toFixed(2)}</TableCell>
                          <TableCell>
                            <Badge variant={order.status === 'confirmed' ? 'default' : 'secondary'}>
                              {order.status}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <Badge variant={order.payment_status === 'paid' ? 'default' : 'destructive'}>
                              {order.payment_status}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <Select onValueChange={(val) => handleUpdateOrderStatus(order.id, val)}>
                              <SelectTrigger className="w-32">
                                <SelectValue placeholder="Update" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="pending">Pending</SelectItem>
                                <SelectItem value="confirmed">Confirmed</SelectItem>
                                <SelectItem value="shipped">Shipped</SelectItem>
                                <SelectItem value="delivered">Delivered</SelectItem>
                              </SelectContent>
                            </Select>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AdminPage;
